import type { MouseEvent } from 'react';
import type { TranslationKey } from '../i18n/en.js';
import { useT } from '../i18n/index.js';
import { CONTENT_VIEWS, pathForView, type ContentView } from '../lib/routes.js';

export type FooterView = ContentView | 'home';

const FOOTER_VIEWS: FooterView[] = ['home', ...CONTENT_VIEWS];

const LABELS: Record<FooterView, TranslationKey> = {
  home: 'nav.home',
  about: 'nav.about',
  contact: 'nav.contact',
  privacy: 'nav.privacy',
  impressum: 'nav.legal',
};

/**
 * The standing navigation: home, about, contact, privacy, legal notice.
 *
 * Real `<a href>` elements rather than buttons, and that is the whole point.
 * A crawler — Google's, or the one AdSense sends before it approves a site —
 * follows links; it does not press buttons and does not guess at React state.
 * Until these pages were reachable by address they may as well not have
 * existed for anyone who was not already clicking around the app.
 *
 * The click handler keeps navigation inside the app for a plain left click and
 * gets out of the way for everything else, so "open in a new tab" still means
 * what it says.
 */
export function SiteFooter({
  current,
  onNavigate,
}: {
  /** The page being shown, marked as the current one and not linked to itself. */
  current: FooterView;
  onNavigate: (view: FooterView) => void;
}) {
  const t = useT();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, view: FooterView) => {
    // Anything but a plain left click means "open this somewhere else" —
    // a new tab, a new window, a download. Intercepting those would quietly
    // break a browser affordance people rely on.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate(view);
  };

  return (
    <>
      <nav className="site-footer" aria-label={t('nav.label')}>
        {FOOTER_VIEWS.map((view) => (
          <a
            key={view}
            className="site-footer__link"
            href={pathForView(view)}
            aria-current={view === current ? 'page' : undefined}
            onClick={(event) => handleClick(event, view)}
          >
            {t(LABELS[view])}
          </a>
        ))}
      </nav>
      {/* TMDB's API terms ask for credit wherever its data is shown, and
        JustWatch's streaming data is passed on on the same condition. The
        full notice and the links are on the about page. */}
      <p className="site-footer__attribution small muted">{t('nav.attribution')}</p>
    </>
  );
}
